import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdPerson,
  MdWork,
  MdSupervisorAccount,
  MdCreditCard,
  MdExtension,
  MdSubtitles,
  MdLabel,
  MdSchedule,
  MdScheduleSend,
  MdEmail,
  MdNote,
  MdPublic,
  MdHelp,
  MdAttachMoney,
  MdExitToApp,
  MdLock,
  MdDeleteForever,
  MdPolicy,
} from 'react-icons/md';
import { productFeature } from 'lib/utils/productFeature';
import {
  AIAssistantsIcon,
  PersonalizationIcon,
  QuickVideosIcon,
} from 'assets/icons/ai';

const iconSize = 18;
const additionalIconSize = 16;

export const useProfileRoutes = () => {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => ({
      profile: {
        title: t('My Profile'),
        icon: <MdPerson size={iconSize} color={'inherit'} />,
        path: '/profile/Profile_EditUserInfo',
        isDisabled: false,
      },
      company: {
        title: t('Company Profile'),
        icon: <MdWork size={iconSize} color={'inherit'} />,
        path: '/profile/Profile_EditCompanyInfo',
        isDisabled: false,
      },
      manageUsers: {
        title: t('Manage Users'),
        icon: <MdSupervisorAccount size={iconSize} color={'inherit'} />,
        path: '/users/list',
        additionalIcon: <MdLock size={additionalIconSize} color={'#001B53'} />,
        isDisabled: false,
      },
      billing: {
        title: t('Billing'),
        icon: <MdCreditCard size={iconSize} color={'inherit'} />,
        path: '/billing',
        isDisabled: false,
      },
      addons: {
        title: t('Add-ons'),
        icon: <MdExtension size={iconSize} color={'inherit'} />,
        path: '/profile/add-ons',
        isDisabled: false,
      },
      videos: {
        title: t('Videos'),
        isLabel: true,
      },
      teleprompter: {
        title: t('Teleprompter'),
        icon: <MdSubtitles size={iconSize} color={'inherit'} />,
        path: '/profile/teleprompter',
        additionalIcon: <MdLock size={additionalIconSize} color={'#001B53'} />,
        productFeatureId: productFeature.TELEPROMPTER,
        isDisabled: false,
      },
      videoAttributes: {
        title: t('Video Attributes'),
        icon: <MdLabel size={iconSize} color={'inherit'} />,
        path: '/profile/Profile_VideoAttributes',
        additionalIcon: <MdLock size={additionalIconSize} color={'#001B53'} />,
        productFeatureId: productFeature.VIDEO_ATTRIBUTES,
        isDisabled: false,
      },
      videoExpiration: {
        title: t('Video Expiration'),
        icon: <MdSchedule size={iconSize} color={'inherit'} />,
        path: '/profile/Profile_VideoDeletion',
        additionalIcon: <MdLock size={additionalIconSize} color={'#001B53'} />,
        productFeatureId: productFeature.VIDEO_EXPIRATION,
        isDisabled: false,
      },
      emails: {
        title: t('Emails'),
        isLabel: true,
      },
      schedule: {
        title: t('Schedules'),
        icon: <MdScheduleSend size={iconSize} color={'inherit'} />,
        path: '/profile/schedules',
        additionalIcon: <MdLock size={additionalIconSize} color={'#001B53'} />,
        productFeatureId: productFeature.SCHEDULES,
        isDisabled: false,
      },
      emailBatches: {
        title: t('Email Batches'),
        icon: <MdEmail size={iconSize} color={'inherit'} />,
        path: '/profile/Email_Batches',
        isDisabled: false,
      },
      services: {
        title: t('Services'),
        icon: <MdExtension color={'inherit'} size={iconSize} />,
        path: '/profile/services',
        isDisabled: false,
      },
      templates: {
        title: t('Templates'),
        icon: <MdNote color={'inherit'} size={iconSize} />,
        path: '/profile/templates',
        additionalIcon: <MdLock size={additionalIconSize} color={'#001B53'} />,
        productFeatureId: productFeature.TEMPLATES,
        isDisabled: false,
      },
      others: {
        title: t('Others'),
        isLabel: true,
      },

      socialProfile: {
        title: t('Social Profiles'),
        icon: <MdPublic size={iconSize} color={'inherit'} />,
        path: '/profile/social-profiles',
        additionalIcon: <MdLock size={additionalIconSize} color={'#001B53'} />,
        productFeatureId: productFeature.YOUTUBE,
        isDisabled: false,
      },
      help: {
        title: t('Help Center'),
        icon: <MdHelp size={iconSize} color={'inherit'} />,
        path: '/help',
        isDisabled: false,
      },
      affiliate: {
        title: t('Affiliate'),
        icon: <MdAttachMoney size={iconSize} color={'inherit'} />,
        path: '/profile/Profile_AffiliateApply',
        isDisabled: false,
      },
      recentlyDeleted: {
        title: t('Recently Deleted'),
        icon: <MdDeleteForever size={iconSize} color={'inherit'} />,
        path: '/profile/recently-deleted',
        isDisabled: false,
      },

      logout: {
        title: t('Log Out'),
        icon: <MdExitToApp size={iconSize} color={'inherit'} />,
        path: '/Sign_Out',
        isDisabled: false,
        className: 'logout',
      },
      aiConfiguration: {
        title: 'AI Configuration',
        isLabel: true,
      },
      aiAssistants: {
        title: 'AI Assistants',
        icon: <AIAssistantsIcon color={'inherit'} />,
        path: '/ai-configuration/ai-assistants',
      },
      personalization: {
        title: 'Personalization',
        icon: <PersonalizationIcon color={'inherit'} />,
        path: '/ai-configuration/personalization',
      },
      quickVideos: {
        title: 'Quick Videos',
        icon: <QuickVideosIcon color={'inherit'} />,
        path: '/ai-configuration/quick-videos',
      },
      termsAndConditions: {
        title: 'Terms and Conditions',
        icon: <MdPolicy size={20} />,
        path: '/profile/terms-and-conditions',
        isDisabled: false,
      },
    }),
    [i18n.language]
  );
};

export const useAdminUserItems = () => {
  const routes = useProfileRoutes();

  return useCallback(
    (isVinReelsEnabled: boolean = false) => {
      const VINREELS_ITEMS = isVinReelsEnabled
        ? [
            routes.aiConfiguration,
            routes.aiAssistants,
            routes.personalization,
            routes.quickVideos,
          ]
        : [];

      return [
        routes.profile,
        routes.company,
        routes.manageUsers,
        routes.billing,
        routes.addons,
        ...VINREELS_ITEMS,
        routes.videos,
        routes.teleprompter,
        routes.videoAttributes,
        routes.videoExpiration,
        routes.emails,
        routes.templates,
        routes.schedule,
        routes.emailBatches,
        routes.others,
        routes.socialProfile,
        routes.help,
        routes.affiliate,
        routes.recentlyDeleted,
        routes.termsAndConditions,
        routes.logout,
      ];
    },
    [routes]
  );
};
